import { useLowDataMode, useStationMode } from '@hooks/station';
import { stationSettingsUrlParams } from '@utils/station';
import {
  sendStartHostMessage,
  /*isWebSocketCommunication,*/ sendStartOverMessage,
} from '@utils/station';
import { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const STATION_VOLUME_KEY = 'napster_station_volume';
const STATION_MUTED_KEY = 'napster_station_muted';

function getStoredVolume(): number {
  if (typeof window === 'undefined') return 100;
  const v = localStorage.getItem(STATION_VOLUME_KEY);
  if (v == null) return 100;
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n >= 0 && n <= 100 ? n : 100;
}

function getStoredMuted(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(STATION_MUTED_KEY) === 'true';
}

export const StationSettings = memo(() => {
  const { t } = useTranslation();
  const isStationMode = useStationMode();
  //const isWebSocket = isWebSocketCommunication();
  const [isLowDataMode, setLowDataMode] = useLowDataMode();
  const [isOpen, setIsOpen] = useState(false);
  const [volume, setVolume] = useState(getStoredVolume);
  const [isMuted, setIsMuted] = useState(getStoredMuted);

  // Sync stored volume/mute to native companion on mount (e.g. after reload)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const storedVol = getStoredVolume();
    const storedMuted = getStoredMuted();
    if (window.updateNativeCompanionVolume) {
      window.updateNativeCompanionVolume(storedVol / 100);
    }
    if (window.muteNativeCompanion) {
      window.muteNativeCompanion(storedMuted);
    }
  }, []);

  // Apply volume change
  const handleVolumeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newVolume = parseInt(e.target.value);
      setVolume(newVolume);
      try {
        localStorage.setItem(STATION_VOLUME_KEY, String(newVolume));
      } catch {
        // ignore
      }
      const normalizedValue = newVolume / 100;
      if (typeof window !== 'undefined' && window.updateNativeCompanionVolume) {
        window.updateNativeCompanionVolume(normalizedValue);
      }
    },
    [],
  );

  // Toggle mute
  const handleMuteToggle = useCallback(() => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    try {
      if (newMuted) {
        localStorage.setItem(STATION_MUTED_KEY, 'true');
      } else {
        localStorage.removeItem(STATION_MUTED_KEY);
      }
    } catch {
      // ignore
    }
    if (typeof window !== 'undefined' && window.muteNativeCompanion) {
      window.muteNativeCompanion(newMuted);
    }
  }, [isMuted]);

  // Start host
  const handleStartHost = useCallback(() => {
    sendStartHostMessage();
  }, []);

  // Reload to home with URL parameters
  const handleReload = useCallback(() => {
    // Send bridge message
    sendStartOverMessage();

    // Every station setting comes from one place, so a reload can't drop one
    const params = stationSettingsUrlParams();

    // Platform is set by the native shell rather than by a station parameter
    const platform = localStorage.getItem('napster_platform');
    if (platform) {
      params.set('platform', platform);
    }

    // Navigate to home with parameters
    const homeUrl = `/${params.toString() ? `?${params.toString()}` : ''}`;
    window.location.href = homeUrl;
  }, []);

  // Only show when BOTH station mode AND websocket communication are enabled
  if (!isStationMode /*|| !isWebSocket*/) {
    return null;
  }

  return (
    <>
      <button
        className="fixed bottom-5 left-5 w-12 h-12 rounded-full bg-[rgba(30,30,30,0.95)] border border-white/10 text-white cursor-pointer flex items-center justify-center z-[999999] transition-all duration-300 backdrop-blur-[10px] shadow-[0_4px_12px_rgba(0,0,0,0.3)] hover:bg-[rgba(40,40,40,0.95)] hover:scale-105 hover:shadow-[0_6px_16px_rgba(0,0,0,0.4)] sm:bottom-4 sm:left-4 sm:w-11 sm:h-11"
        onClick={() => setIsOpen(!isOpen)}
        title={t('station.settings')}>
        <svg
          viewBox="0 0 24 24"
          width="24"
          height="24"
          fill="currentColor"
          className="transition-transform duration-300 hover:rotate-45">
          <path d="M19.14,12.94c0.04-0.31,0.06-0.63,0.06-0.94c0-0.31-0.02-0.63-0.06-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.37,4.8,11.69,4.8,12s0.02,0.63,0.06,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z" />
        </svg>
      </button>

      {isOpen && (
        <div className="fixed bottom-20 left-5 w-[280px] bg-[rgba(30,30,30,0.98)] border border-white/10 rounded-xl p-4 z-[999998] backdrop-blur-[20px] shadow-[0_8px_24px_rgba(0,0,0,0.4)] animate-stationSettingSlideUp sm:bottom-[72px] sm:left-4 sm:w-[calc(100vw-32px)] sm:max-w-[320px]">
          <div className="mb-4">
            <label className="block text-white/70 text-xs font-medium mb-2 uppercase tracking-[0.5px]">
              {t('station.companionVolume')}
            </label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="0"
                max="100"
                value={volume}
                onChange={handleVolumeChange}
                className="flex-1 h-1 rounded-sm bg-white/10 outline-none appearance-none [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-[0_2px_6px_rgba(0,0,0,0.3)] [&::-webkit-slider-thumb]:transition-all [&::-webkit-slider-thumb]:duration-200 [&::-webkit-slider-thumb]:hover:scale-110 [&::-webkit-slider-thumb]:hover:shadow-[0_3px_8px_rgba(0,0,0,0.4)] [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:shadow-[0_2px_6px_rgba(0,0,0,0.3)] [&::-moz-range-thumb]:transition-all [&::-moz-range-thumb]:duration-200 [&::-moz-range-thumb]:hover:scale-110 [&::-moz-range-thumb]:hover:shadow-[0_3px_8px_rgba(0,0,0,0.4)]"
              />
              <span className="text-white text-sm font-medium min-w-[42px] text-right font-mono">
                {(volume / 100).toFixed(2)}
              </span>
            </div>
          </div>

          <div className="mb-4">
            <button
              className="w-full py-2.5 px-4 rounded-lg border-0 text-[13px] font-semibold cursor-pointer transition-all duration-200 bg-green-500/20 text-green-500 hover:bg-green-500/30 hover:-translate-y-px active:translate-y-0"
              onClick={handleStartHost}>
              {t('station.startHost')}
            </button>
          </div>

          <div className="mb-4">
            <label className="block text-white/70 text-xs font-medium mb-2 uppercase tracking-[0.5px]">
              {t('station.lowDataMode')}
            </label>
            <p className="text-white/50 text-xs mb-2">
              {t('station.lowDataModeDesc')}
            </p>
            <button
              className={`w-full py-2.5 px-4 rounded-lg border-0 text-[13px] font-semibold cursor-pointer transition-all duration-200 ${
                isLowDataMode
                  ? 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30'
                  : 'bg-white/10 text-white hover:bg-white/15'
              } hover:-translate-y-px active:translate-y-0`}
              onClick={() => setLowDataMode(!isLowDataMode)}>
              {isLowDataMode ? t('station.on') : t('station.off')}
            </button>
          </div>

          <div>
            <div className="flex gap-2">
              <button
                className={`flex-1 py-2.5 px-4 rounded-lg border-0 text-[13px] font-semibold cursor-pointer transition-all duration-200 ${
                  isMuted
                    ? 'bg-red-500/20 text-red-500 hover:bg-red-500/30'
                    : 'bg-white/10 text-white hover:bg-white/15'
                } hover:-translate-y-px active:translate-y-0`}
                onClick={handleMuteToggle}>
                {isMuted ? t('station.unmute') : t('station.mute')}
              </button>
              <button
                className="flex-1 py-2.5 px-4 rounded-lg border-0 text-[13px] font-semibold cursor-pointer transition-all duration-200 bg-blue-500/20 text-blue-500 hover:bg-blue-500/30 hover:-translate-y-px active:translate-y-0"
                onClick={handleReload}>
                {t('station.reload')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

StationSettings.displayName = 'StationSettings';
